import { create } from 'zustand';
import type { Device, DeviceGroup, Parameter } from '../api/types';
import { mockAPI } from '../api/mockApi';

interface DeviceState {
  devices: Device[];
  deviceGroups: DeviceGroup[];
  currentDevice: Device | null;
  isLoading: boolean;
  error: string | null;
  selectedRoom: string | null;
  isConnected: boolean;
  
  fetchDevices: () => Promise<void>;
  fetchDevice: (deviceId: string) => Promise<void>;
  setDeviceValue: (deviceId: string, parameter: Parameter) => Promise<boolean>;
  editDevice: (deviceId: string, newName?: string, newRoom?: string) => Promise<boolean>;
  deleteDevice: (deviceId: string) => Promise<boolean>;
  updateDeviceParameter: (deviceId: string, paramName: string, value: string) => Promise<void>;
  setSelectedRoom: (room: string | null) => void;
  updateDeviceInList: (device: Device) => void;
  clearError: () => void;
}

export const useDeviceStore = create<DeviceState>((set, get) => ({
  devices: [],
  deviceGroups: [],
  currentDevice: null,
  isLoading: false,
  error: null,
  selectedRoom: null,
  isConnected: true,

  fetchDevices: async () => {
    set({ isLoading: true, error: null });
    
    try {
      const result = await mockAPI.getDeviceList(1);
      const allDevices = result[0]?.value || [];
      
      set({ 
        devices: allDevices,
        deviceGroups: result,
        isLoading: false,
        isConnected: true
      });
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : 'Failed to fetch devices',
        isLoading: false,
        isConnected: false
      });
    }
  },

  fetchDevice: async (deviceId: string) => {
    set({ isLoading: true, error: null });
    
    try {
      const device = await mockAPI.getDevice(1, deviceId);
      set({ 
        currentDevice: device,
        isLoading: false 
      });
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : 'Failed to fetch device',
        isLoading: false 
      });
    }
  },

  setDeviceValue: async (deviceId: string, parameter: Parameter) => {
    try {
      await mockAPI.setDeviceValue(1, deviceId, parameter);
      
      const { devices, currentDevice } = get();
      
      const updatedDevices = devices.map(d => {
        if (d.deviceId === deviceId) {
          const updatedParams = d.parameters.map(p => 
            p.name === parameter.name ? { ...p, ...parameter } : p
          );
          return { ...d, parameters: updatedParams };
        }
        return d;
      });
      
      let updatedCurrent = currentDevice;
      if (currentDevice?.deviceId === deviceId) {
        const updatedParams = currentDevice.parameters.map(p =>
          p.name === parameter.name ? { ...p, ...parameter } : p
        );
        updatedCurrent = { ...currentDevice, parameters: updatedParams };
      }
      
      set({ 
        devices: updatedDevices,
        currentDevice: updatedCurrent
      });
      
      return true;
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : 'Failed to set device value'
      });
      return false;
    }
  },

  editDevice: async (deviceId: string, newName?: string, newRoom?: string) => {
    try {
      await mockAPI.editDevice(1, deviceId, newName, newRoom);
      
      const { devices, currentDevice } = get();
      
      const updatedDevices = devices.map(d => {
        if (d.deviceId === deviceId) {
          return { 
            ...d, 
            customName: newName !== undefined ? newName : d.customName,
            room: newRoom !== undefined ? newRoom : d.room
          };
        }
        return d;
      });
      
      let updatedCurrent = currentDevice;
      if (currentDevice?.deviceId === deviceId) {
        updatedCurrent = { 
          ...currentDevice,
          customName: newName !== undefined ? newName : currentDevice.customName,
          room: newRoom !== undefined ? newRoom : currentDevice.room
        };
      }
      
      set({ 
        devices: updatedDevices,
        currentDevice: updatedCurrent
      });
      
      return true;
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : 'Failed to edit device'
      });
      return false;
    }
  },

  deleteDevice: async (deviceId: string) => {
    try {
      await mockAPI.deleteDevice(1, deviceId);
      
      const { devices } = get();
      const updatedDevices = devices.filter(d => d.deviceId !== deviceId);
      
      set({ 
        devices: updatedDevices,
        currentDevice: null
      });
      
      return true;
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : 'Failed to delete device'
      });
      return false;
    }
  },

  updateDeviceParameter: async (deviceId: string, paramName: string, value: string) => {
    const { devices } = get();
    const device = devices.find(d => d.deviceId === deviceId);
    
    if (!device) return;
    
    const parameter = device.parameters.find(p => p.name === paramName);
    if (!parameter) return;
    
    const updatedParam = { ...parameter, stringValue: value };
    await get().setDeviceValue(deviceId, updatedParam);
  },

  setSelectedRoom: (room: string | null) => {
    set({ selectedRoom: room });
  },

  updateDeviceInList: (device: Device) => {
    const { devices, currentDevice } = get();
    
    const updatedDevices = devices.map(d => 
      d.deviceId === device.deviceId ? device : d
    );
    
    const updatedCurrent = currentDevice?.deviceId === device.deviceId 
      ? device 
      : currentDevice;
    
    set({ 
      devices: updatedDevices,
      currentDevice: updatedCurrent
    });
  },

  clearError: () => {
    set({ error: null });
  }
}));
